import React from "react";
import { motion } from "framer-motion";
import { useDashTheme } from "../../hooks/useDashTheme";
import { Shimmer } from "../../components/StatCard";
import { ProjectCard } from "./ProjectCard";

// The grid view. Cards reflow on `auto-fill` rather than a fixed column count,
// so the same markup works from a narrow split pane out to a wide monitor.
export function ProjectsGrid({ projects, loading, skeletonCards = 6, emptyState, stats = {}, ...cardProps }) {
  const t = useDashTheme();

  if (loading) {
    return (
      <div style={gridStyle(t)}>
        {Array.from({ length: skeletonCards }).map((_, i) => <CardSkeleton key={i} t={t} />)}
      </div>
    );
  }

  if (!projects.length) return emptyState || null;

  return (
    <motion.div variants={t.stagger.container} initial="initial" animate="animate" style={gridStyle(t)}>
      {/* The stats map is keyed by project id; the card takes its own row, not
          the whole map, so it can't accidentally read another project's goals. */}
      {projects.map((p) => {
        const id = String(p._id || p.id);
        return <ProjectCard key={id} project={p} stat={stats[id]} {...cardProps} />;
      })}
    </motion.div>
  );
}

const gridStyle = (t) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(324px, 1fr))",
  gap: t.s[4],
  alignItems: "stretch",
});

// Mirrors the real card's proportions — band, two text lines, two meters, a
// footer — so the layout doesn't jump when the data lands.
function CardSkeleton({ t }) {
  return (
    <div style={{
      background: t.c.surface, border: `1px solid ${t.c.border}`, borderRadius: t.r.md,
      boxShadow: t.shadow.sm, overflow: "hidden",
    }}>
      <div style={{ height: 74, background: t.c.surfaceMuted }} />
      <div style={{ padding: `${t.s[3]}px ${t.s[4]}px` }}>
        <Shimmer w="62%" h={15} />
        <div style={{ height: 10 }} />
        <Shimmer w="100%" h={11} />
        <div style={{ height: 6 }} />
        <Shimmer w="78%" h={11} />
        <div style={{ height: 18 }} />
        <Shimmer w="100%" h={6} />
        <div style={{ height: 18 }} />
        <Shimmer w="100%" h={6} />
      </div>
      <div style={{ padding: `${t.s[3]}px ${t.s[4]}px`, borderTop: `1px solid ${t.c.border}`, background: t.c.surfaceMuted }}>
        <Shimmer w={110} h={26} radius={99} />
      </div>
    </div>
  );
}
